import React, { useState, useRef, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack";
import toast from "react-hot-toast";
import { Dialog } from "@headlessui/react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

const MIN_SELECTION = 20;
const TARGET_QR_DPI = 300;
const MIN_TARGET_PX = 300;

const ImageCropper = ({ imageSrc, onCrop, onCancel }) => {
  const imageRef = useRef(null);
  const imageRefs = useRef({});
  const containerRef = useRef(null);
  const { t } = useTranslation();

  const [cropArea, setCropArea] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [pdfPreviewPages, setPdfPreviewPages] = useState([]);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const dragPageRef = useRef(1);
  const startPosRef = useRef({ x: 0, y: 0 });
  const cropAreaRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const resizeHandleRef = useRef(null);

  const getPdfDataU8 = async (src) => {
    if (src.startsWith("data:application/pdf")) {
      const b64 = src.split(",")[1];
      return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    }

    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  };

  async function renderPdfSelectionToBlob(
    pdfDataU8,
    pageNumber,
    sel,
    previewW,
    previewH,
  ) {
    const pdf = await pdfjsLib.getDocument({ data: pdfDataU8 }).promise;
    const page = await pdf.getPage(pageNumber);

    const scale = TARGET_QR_DPI / 72;
    const viewport = page.getViewport({ scale });

    const sx = sel.x * (viewport.width / previewW);
    const sy = sel.y * (viewport.height / previewH);
    const sw = sel.width * (viewport.width / previewW);
    const sh = sel.height * (viewport.height / previewH);

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    const ctx = canvas.getContext("2d");

    await page.render({
      canvasContext: ctx,
      viewport,
      transform: [1, 0, 0, 1, -sx, -sy],
    }).promise;

    return new Promise((res) => canvas.toBlob(res, "image/png"));
  }

  useEffect(() => {
    if (
      typeof imageSrc === "string" &&
      (imageSrc.startsWith("data:application/pdf") ||
        imageSrc.toLowerCase().includes(".pdf"))
    ) {
      (async () => {
        try {
          setIsPdf(true);
          setPdfPreviewPages([]);
          setCropArea(null);
          setActivePageNumber(1);

          const pdfData = await getPdfDataU8(imageSrc);
          const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
          const previewW = containerRef.current?.clientWidth || 800;
          const renderedPages = [];

          for (
            let pageNumber = 1;
            pageNumber <= pdf.numPages;
            pageNumber += 1
          ) {
            const page = await pdf.getPage(pageNumber);
            const baseViewport = page.getViewport({ scale: 1 });
            const scale = Math.min(previewW / baseViewport.width, 2);
            const viewport = page.getViewport({ scale });

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: ctx, viewport }).promise;
            renderedPages.push({
              pageNumber,
              src: canvas.toDataURL(),
            });
          }

          setPdfPreviewPages(renderedPages);
        } catch (err) {
          console.error(err);
          toast.error(t("verify:failed_load_pdf"));
          onCancel();
        }
      })();
    } else {
      setIsPdf(false);
      setPdfPreviewPages([]);
      setCropArea(null);
      setActivePageNumber(1);
    }
  }, [imageSrc, onCancel, t]);

  const getActiveImageElement = (pageNumber = activePageNumber) => {
    if (isPdf) {
      return imageRefs.current[pageNumber] || null;
    }
    return imageRef.current;
  };

  const normalizeArea = (area) => {
    const x2 = area.x + area.width;
    const y2 = area.y + area.height;
    return {
      ...area,
      x: Math.min(area.x, x2),
      y: Math.min(area.y, y2),
      width: Math.abs(area.width),
      height: Math.abs(area.height),
    };
  };

  useEffect(() => {
    cropAreaRef.current = cropArea;
  }, [cropArea]);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    isResizingRef.current = isResizing;
  }, [isResizing]);

  useEffect(() => {
    resizeHandleRef.current = resizeHandle;
  }, [resizeHandle]);

  const getImageCoordinates = (
    clientX,
    clientY,
    pageNumber = activePageNumber,
  ) => {
    const img = getActiveImageElement(pageNumber);
    const container = containerRef.current;
    if (!img || !container) return null;

    const imgRect = img.getBoundingClientRect();

    const x = Math.max(0, Math.min(clientX - imgRect.left, img.clientWidth));
    const y = Math.max(0, Math.min(clientY - imgRect.top, img.clientHeight));

    return { x, y };
  };

  const getUIPosition = (x, y, pageNumber = activePageNumber) => {
    const img = getActiveImageElement(pageNumber);
    const container = containerRef.current;
    if (!img || !container) return { uiX: 0, uiY: 0 };

    const containerRect = container.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Convert viewport-relative image coords back to container content coords.
    // Scroll offsets must be added (not subtracted), otherwise overlay drifts after scrolling.
    const uiX = x + (imgRect.left - containerRect.left) + container.scrollLeft;
    const uiY = y + (imgRect.top - containerRect.top) + container.scrollTop;

    return { uiX, uiY };
  };

  const handleMouseDown = (e) => {
    if (!containerRef.current) return;
    e.preventDefault();

    let pageNumber = dragPageRef.current || activePageNumber;
    if (isPdf) {
      const targetImage = e.target.closest("img[data-page-number]");
      if (targetImage) {
        pageNumber = Number(targetImage.dataset.pageNumber) || 1;
      } else if (cropAreaRef.current?.pageNumber) {
        // Resize handles are not inside <img>; use the current crop page.
        pageNumber = cropAreaRef.current.pageNumber;
      } else {
        return;
      }
      setActivePageNumber(pageNumber);
    }
    dragPageRef.current = pageNumber;

    const coords = getImageCoordinates(e.clientX, e.clientY, pageNumber);
    if (!coords) return;
    const { x, y } = coords;

    const currentArea = cropAreaRef.current;
    if (currentArea && currentArea.pageNumber === pageNumber) {
      const handles = getResizeHandles(currentArea);
      for (const [h, coords] of Object.entries(handles)) {
        if (pointInRect(x, y, coords)) {
          setIsResizing(true);
          setResizeHandle(h);
          setIsDragging(false);
          return;
        }
      }
    }

    startPosRef.current = { x, y };
    setCropArea({ x, y, width: 0, height: 0, pageNumber });
    setIsDragging(true);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current && !isResizingRef.current) return;

    const targetPageNumber =
      dragPageRef.current ||
      cropAreaRef.current?.pageNumber ||
      activePageNumber;
    if (!getActiveImageElement(targetPageNumber)) return;
    e.preventDefault();

    const coords = getImageCoordinates(e.clientX, e.clientY, targetPageNumber);
    if (!coords) return;
    const { x, y } = coords;

    if (
      isResizingRef.current &&
      cropAreaRef.current &&
      cropAreaRef.current.pageNumber === targetPageNumber
    ) {
      const area = { ...cropAreaRef.current };
      switch (resizeHandleRef.current) {
        case "tl":
          area.width += area.x - x;
          area.height += area.y - y;
          area.x = x;
          area.y = y;
          break;
        case "tr":
          area.width = x - area.x;
          area.height += area.y - y;
          area.y = y;
          break;
        case "bl":
          area.width += area.x - x;
          area.height = y - area.y;
          area.x = x;
          break;
        case "br":
          area.width = x - area.x;
          area.height = y - area.y;
          break;
        default:
          break;
      }
      setCropArea(normalizeArea({ ...area, pageNumber: targetPageNumber }));
    } else if (isDraggingRef.current) {
      const start = startPosRef.current;
      setCropArea(
        normalizeArea({
          x: Math.min(start.x, x),
          y: Math.min(start.y, y),
          width: Math.abs(x - start.x),
          height: Math.abs(y - start.y),
          pageNumber: targetPageNumber,
        }),
      );
    }
  };

  const stopActions = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const onMouseMove = (event) => {
      if (!isDraggingRef.current && !isResizingRef.current) return;

      const targetPageNumber =
        dragPageRef.current ||
        cropAreaRef.current?.pageNumber ||
        activePageNumber;
      if (!getActiveImageElement(targetPageNumber)) return;
      event.preventDefault();

      const coords = getImageCoordinates(
        event.clientX,
        event.clientY,
        targetPageNumber,
      );
      if (!coords) return;
      const { x, y } = coords;

      if (
        isResizingRef.current &&
        cropAreaRef.current &&
        cropAreaRef.current.pageNumber === targetPageNumber
      ) {
        const area = { ...cropAreaRef.current };
        switch (
          resizeHandleRef.current
          // ... switch cases remain the same
        ) {
        }
        setCropArea(normalizeArea({ ...area, pageNumber: targetPageNumber }));
      } else if (isDraggingRef.current) {
        const start = startPosRef.current;
        setCropArea(
          normalizeArea({
            x: Math.min(start.x, x),
            y: Math.min(start.y, y),
            width: Math.abs(x - start.x),
            height: Math.abs(y - start.y),
            pageNumber: targetPageNumber,
          }),
        );
      }
    };
    const onMouseUp = () => stopActions();

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging, isResizing]);

  const pointInRect = (x, y, r) =>
    x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height;

  const getResizeHandles = (a) => {
    const s = 10;
    return {
      tl: { x: a.x - s / 2, y: a.y - s / 2, width: s, height: s },
      tr: { x: a.x + a.width - s / 2, y: a.y - s / 2, width: s, height: s },
      bl: { x: a.x - s / 2, y: a.y + a.height - s / 2, width: s, height: s },
      br: {
        x: a.x + a.width - s / 2,
        y: a.y + a.height - s / 2,
        width: s,
        height: s,
      },
    };
  };

  const handleCrop = async () => {
    if (
      !cropArea ||
      cropArea.width < MIN_SELECTION ||
      cropArea.height < MIN_SELECTION
    ) {
      toast.error(t("verify:select_valid_area"));
      return;
    }

    try {
      if (isPdf) {
        const pdfDataU8 = await getPdfDataU8(imageSrc);
        const pageNumber = cropArea.pageNumber || activePageNumber || 1;
        const selectedImage = getActiveImageElement(pageNumber);
        if (!selectedImage) {
          toast.error(t("verify:failed_crop_image"));
          return;
        }

        const previewW = selectedImage.clientWidth;
        const previewH = selectedImage.clientHeight;

        const blob = await renderPdfSelectionToBlob(
          pdfDataU8,
          pageNumber,
          cropArea,
          previewW,
          previewH,
        );
        onCrop(blob);
      } else {
        const img = imageRef.current;
        const previewW = img.clientWidth;
        const previewH = img.clientHeight;
        const scaleX = img.naturalWidth / previewW;
        const scaleY = img.naturalHeight / previewH;

        const dpr = window.devicePixelRatio || 1;
        const scaleUp = Math.max(
          MIN_TARGET_PX / cropArea.width,
          MIN_TARGET_PX / cropArea.height,
          1,
        );

        const canvas = document.createElement("canvas");
        canvas.width = Math.round(cropArea.width * scaleUp * dpr);
        canvas.height = Math.round(cropArea.height * scaleUp * dpr);
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;

        ctx.drawImage(
          img,
          cropArea.x * scaleX,
          cropArea.y * scaleY,
          cropArea.width * scaleX,
          cropArea.height * scaleY,
          0,
          0,
          canvas.width,
          canvas.height,
        );

        canvas.toBlob(
          (blob) => {
            blob ? onCrop(blob) : toast.error(t("verify:failed_crop_image"));
          },
          "image/jpeg",
          0.95,
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(t("verify:cropping_failed_retry"));
    }
  };

  if (isPdf && pdfPreviewPages.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[1000]">
        <div className="text-white text-xl">{t("verify:loading_pdf")}</div>
      </div>
    );
  }

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      className="relative z-[1000]"
      initialFocus={containerRef}
      static
    >
      <div
        className="fixed inset-0 bg-[rgba(240,240,240,0.45)] backdrop-blur-sm"
        aria-hidden="true"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel id="image-cropper-modal" className="bg-white rounded-2xl p-4  max-w-6xl  max-h-[90vh] flex flex-col w-full">
          <Dialog.Title className="flex flex-col gap-0 justify-start w-full mb-3">
            <p className="text-gray-800 font-semibold text-lg">
              {t("verify:select_qr_area")}
              <p className="text-gray-800 font-normal text-xs">
                {t("verify:drag_resize_rectangle")}
              </p>
            </p>
            <div className="p-3 bg-blue-50 border-none rounded-lg mt-3">
              <div className="flex items-center">
                <div className="mr-3 text-cfPrimary">
                  <Info className="h-5 w-5" />
                </div>
                <p className="text-sm text-cfPrimary">
                  {t("verify:select_image_region_qr")}
                </p>
              </div>
            </div>
          </Dialog.Title>

          <div
            ref={containerRef}
            className={`relative w-full max-h-[70vh] overflow-auto border border-[#a7a7a7] bg-[#ececec] focus:outline-none ${
              cropArea ? "cursor-default" : "cursor-crosshair"
            }`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopActions}
          >
            {!isPdf && imageSrc && (
              <img
                ref={imageRef}
                src={imageSrc}
                alt="preview"
                data-page-number={1}
                className="block max-w-full max-h-[calc(70vh-4px)] mx-auto select-none"
                draggable="false"
                onDragStart={(e) => e.preventDefault()}
              />
            )}
            {isPdf && (
              <div className="flex flex-col items-center gap-4 py-3">
                {pdfPreviewPages.map((page) => (
                  <img
                    key={`cropper-pdf-page-${page.pageNumber}`}
                    ref={(el) => {
                      imageRefs.current[page.pageNumber] = el;
                    }}
                    src={page.src}
                    alt={`preview-page-${page.pageNumber}`}
                    data-page-number={page.pageNumber}
                    className="block max-w-full mx-auto select-none border border-gray-300 bg-white"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                  />
                ))}
              </div>
            )}

            {cropArea &&
              (() => {
                const { uiX, uiY } = getUIPosition(
                  cropArea.x,
                  cropArea.y,
                  cropArea.pageNumber,
                );
                const { uiX: endX, uiY: endY } = getUIPosition(
                  cropArea.x + cropArea.width,
                  cropArea.y + cropArea.height,
                  cropArea.pageNumber,
                );
                const uiWidth = endX - uiX;
                const uiHeight = endY - uiY;

                return (
                  <>
                    <div
                      className="absolute bg-[rgba(65,105,225,0.3)] border-2 border-dashed border-[#4169e1] pointer-events-none"
                      style={{
                        left: `${uiX}px`,
                        top: `${uiY}px`,
                        width: `${uiWidth}px`,
                        height: `${uiHeight}px`,
                      }}
                    />
                    {Object.entries(getResizeHandles(cropArea)).map(
                      ([k, r]) => {
                        const { uiX: handleX, uiY: handleY } = getUIPosition(
                          r.x,
                          r.y,
                          cropArea.pageNumber,
                        );
                        return (
                          <div
                            key={k}
                            className="absolute bg-royalblue rounded-full pointer-events-auto z-10"
                            style={{
                              left: `${handleX}px`,
                              top: `${handleY}px`,
                              width: `${r.width}px`,
                              height: `${r.height}px`,
                              cursor: `${k}-resize`,
                            }}
                          />
                        );
                      },
                    )}
                  </>
                );
              })()}
          </div>

          <div className="flex justify-end gap-4 w-full mt-3">
            <button
              id="image-cropper-cancel-button"
              onClick={onCancel}
              className="px-4 py-2 rounded-[8px] hover:!bg-[#f7f7f7] border-[1.5px] border-[#D0D5DD]"
            >
              {t("verify:cancel")}
            </button>
            <button
              id="image-cropper-done-button"
              onClick={handleCrop}
              className="px-4 py-2 bg-cfPrimary text-white rounded-[8px] hover:bg-cfPrimaryDark"
            >
              {t("verify:done")}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ImageCropper;
